import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { LuLock } from "react-icons/lu";
import { ACTIONS } from "../../_config";
import { useConsentApi } from "../../store/api/hooks/consentApiHook";
import { useUserSelector } from "../../store/selectors/userSelector";
import Button from "../elements/button";
import { useConsentSignOut } from "./useConsentSignOut";

/**
 * Full-screen lock for non-owner users until the owner accepts. Re-checks the
 * acceptance on mount, on window focus and via Retry; any error keeps the screen
 * locked (fail closed) and only an API answer of `accepted: true` unlocks it.
 */
const NotActivatedScreen: React.FC = () => {
  const { t } = useTranslation("consent");
  const dispatch = useDispatch();
  const user: any = useUserSelector();
  const consentApi = useConsentApi();
  const { signOut, isSigningOut } = useConsentSignOut();

  const [supportNumber, setSupportNumber] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [isError, setIsError] = useState(false);

  const checkStatus = useCallback(() => {
    setIsChecking(true);
    setIsError(false);
    consentApi.getMerchantConsent(
      {},
      (resp: any) => {
        setIsChecking(false);
        if (resp?.data?.accepted === true) {
          dispatch({ type: ACTIONS.USER.USER, payload: { ...user, isTermAccepted: true } });
        }
      },
      () => {
        setIsChecking(false);
        setIsError(true);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user]);

  useEffect(() => {
    checkStatus();
    consentApi.getConsentContent(
      {},
      (resp: any) => setSupportNumber(resp?.data?.supportNumber ?? null),
      () => {}
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener("focus", checkStatus);
    return () => window.removeEventListener("focus", checkStatus);
  }, [checkStatus]);

  return (
    <div className="flex items-center justify-center w-screen h-screen px-4 bg-white">
      <div className="flex flex-col items-center max-w-lg gap-3 text-center">
        <div className="flex items-center justify-center w-12 h-12 text-red-500 rounded-xl bg-red-50">
          <LuLock size={22} />
        </div>
        <h1 className="text-xl font-semibold text-gray-900">{t("notActivated.title")}</h1>
        <p className="text-base leading-relaxed text-gray-500">{t("notActivated.body")}</p>
        <div className="text-sm text-gray-500">
          {t("notActivated.hint")}
          {supportNumber && (
            <>
              {" "}
              {t("notActivated.support")} — <span className="font-semibold text-blue-600">{supportNumber}</span>
            </>
          )}
        </div>
        {isError && (
          <p className="text-sm text-red-600" role="alert">
            {t("notActivated.checkError")}
          </p>
        )}
        <div className="flex items-center gap-3 mt-2">
          <Button theme="secondary" disabled={isChecking} onClick={checkStatus}>
            {isChecking ? t("notActivated.checking") : t("retry")}
          </Button>
          <Button theme="secondary" disabled={isSigningOut} onClick={signOut}>
            {isSigningOut ? t("signingOut") : t("signOut")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotActivatedScreen;
